import { useQuery } from '@tanstack/react-query';
import { Option, SearchOption, TableListData } from '../../types/common/table';
import { useTableOption } from './useTableOption';

interface UseTableListProps<T, F> {
  queryKey: string[];
  fetcher: (option: SearchOption<F>) => Promise<TableListData<T>>;
  initialOption?: Option<F>;
}

export const useTableList = <T extends Record<string, unknown>, F extends Record<string, unknown>>({
  queryKey,
  fetcher,
  initialOption,
}: UseTableListProps<T, F>) => {
  const {
    sortOption,
    pagination: paginationOption,
    filterOption,
    changeOption,
    resetOption,
  } = useTableOption(initialOption);

  const option: SearchOption<F> = {
    page: paginationOption.pageNum + 1,
    pageSize: paginationOption.pageSize,
    sort: Object.entries(sortOption).map(([name, isDesc]) => (isDesc ? `-${name}` : name)),
    ...filterOption,
  };

  const { data } = useQuery([...queryKey, option], () => fetcher(option));

  return {
    totalCount: data?.totalCount || 0,
    list: data?.list || [],
    pagination: paginationOption,
    sortOption,
    filterOption,
    refetchList: changeOption,
    resetOption,
  };
};
